/**
 * IntegrationWorkspace - External System Integration Management
 *
 * Manages integration channels, messages, message types, and flows.
 * Provides monitoring dashboard and operational controls.
 *
 * Tabs:
 * - Dashboard: Stats, health overview, recent activity
 * - Channels: CRUD for integration channels
 * - Message Types: Field mappings and transformations
 * - Messages: Queue browser with retry/DLQ actions
 * - Flows: Integration pipeline definitions
 * - Inbound Simulator: Test inbound messages from external systems
 * - Outbound Log: View captured outbound messages
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as RetryIcon,
  Delete as DlqIcon,
  Replay as RequeueIcon,
  CheckCircle as HealthyIcon,
  Error as UnhealthyIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  PowerSettingsNew as ToggleIcon,
} from '@mui/icons-material';
import IntegrationService from '../../../services/IntegrationService';
import InboundSimulatorTab from './InboundSimulatorTab';
import OutboundLogTab from './OutboundLogTab';
import FileImportTab from './integration/FileImportTab';
import MessageTypeDesigner from './integration/MessageTypeDesigner';

// Tab panel component
const TabPanel = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`integration-tabpanel-${index}`}
    {...other}
    sx={{ flex: 1, overflow: 'auto', display: value === index ? 'flex' : 'none', flexDirection: 'column' }}
  >
    {value === index && children}
  </Box>
);

// Status chip component
const StatusChip = ({ status, size = 'small' }) => {
  const statusConfig = {
    RECEIVED: { color: 'info', label: 'Received' },
    QUEUED: { color: 'default', label: 'Queued' },
    PROCESSING: { color: 'warning', label: 'Processing' },
    COMPLETED: { color: 'success', label: 'Completed' },
    FAILED: { color: 'error', label: 'Failed' },
    DLQ: { color: 'error', label: 'DLQ' },
    UP: { color: 'success', label: 'Healthy' },
    DOWN: { color: 'error', label: 'Down' },
    DEGRADED: { color: 'warning', label: 'Degraded' },
    UNKNOWN: { color: 'default', label: 'Unknown' },
  };
  const config = statusConfig[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size={size} />;
};

// Health icon component
const HealthIcon = ({ status }) => {
  switch (status) {
    case 'UP':
      return <HealthyIcon color="success" />;
    case 'DOWN':
      return <UnhealthyIcon color="error" />;
    case 'DEGRADED':
      return <WarningIcon color="warning" />;
    default:
      return <WarningIcon color="disabled" />;
  }
};

/**
 * Dashboard Tab - Overview statistics and health
 */
const DashboardTab = ({ colors, stats, channels, loading, onRefresh }) => {
  const totalMessages = stats?.totalMessages || 0;
  const failedMessages = stats?.failedMessages || 0;
  const dlqMessages = stats?.dlqMessages || 0;
  const completedMessages = stats?.completedMessages || 0;
  const healthyChannels = channels?.filter(c => c.lastHealthStatus === 'UP').length || 0;
  const totalChannels = channels?.length || 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
          Integration Dashboard
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: colors.textMuted }}>
                Total Messages (24h)
              </Typography>
              <Typography variant="h4" sx={{ color: colors.textPrimary }}>
                {totalMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: colors.textMuted }}>
                Completed
              </Typography>
              <Typography variant="h4" sx={{ color: colors.success }}>
                {completedMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: colors.textMuted }}>
                Failed
              </Typography>
              <Typography variant="h4" sx={{ color: colors.error }}>
                {failedMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
            <CardContent>
              <Typography variant="overline" sx={{ color: colors.textMuted }}>
                Dead Letter Queue
              </Typography>
              <Typography variant="h4" sx={{ color: failedMessages > 0 ? colors.error : colors.textPrimary }}>
                {dlqMessages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Channel Health */}
      <Typography variant="subtitle1" sx={{ color: colors.textPrimary, mb: 1 }}>
        Channel Health ({healthyChannels}/{totalChannels} healthy)
      </Typography>
      <TableContainer component={Paper} sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted }}>Channel</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Type</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Direction</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Status</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Health</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {channels?.map((channel) => (
              <TableRow key={channel.id}>
                <TableCell sx={{ color: colors.textPrimary }}>{channel.channelCode}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{channel.channelType}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{channel.direction}</TableCell>
                <TableCell>
                  <Chip
                    label={channel.isActive ? 'Active' : 'Inactive'}
                    color={channel.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <HealthIcon status={channel.lastHealthStatus || 'UNKNOWN'} />
                </TableCell>
              </TableRow>
            ))}
            {(!channels || channels.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: colors.textMuted, textAlign: 'center' }}>
                  No channels configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Channels Tab - Channel management
 */
const ChannelsTab = ({ colors, channels, loading, onRefresh, onActivate, onDeactivate, onCheckHealth, onCreate }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
          Integration Channels
        </Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
            New Channel
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted }}>Channel Code</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Name</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Type</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Direction</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Endpoint</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Auth</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Status</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Health</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {channels?.map((channel) => (
              <TableRow key={channel.id}>
                <TableCell sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                  {channel.channelCode}
                </TableCell>
                <TableCell sx={{ color: colors.textPrimary }}>{channel.channelName}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{channel.channelType}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{channel.direction}</TableCell>
                <TableCell sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: 11 }}>
                  {channel.endpoint ? (
                    <Tooltip title={channel.endpoint.url}>
                      <span>{channel.endpoint.endpointType}</span>
                    </Tooltip>
                  ) : '-'}
                </TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{channel.effectiveAuthType || channel.authType}</TableCell>
                <TableCell>
                  <Chip
                    label={channel.isActive ? 'Active' : 'Inactive'}
                    color={channel.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <StatusChip status={channel.lastHealthStatus || 'UNKNOWN'} />
                </TableCell>
                <TableCell>
                  <Tooltip title={channel.isActive ? 'Deactivate' : 'Activate'}>
                    <IconButton
                      size="small"
                      onClick={() => channel.isActive ? onDeactivate(channel.channelCode) : onActivate(channel.channelCode)}
                    >
                      <ToggleIcon color={channel.isActive ? 'success' : 'disabled'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Check Health">
                    <IconButton size="small" onClick={() => onCheckHealth(channel.channelCode)}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {(!channels || channels.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: colors.textMuted, textAlign: 'center' }}>
                  No channels configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Messages Tab - Message queue browser
 */
const MessagesTab = ({ colors, loading, onRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const loadMessages = useCallback(async () => {
    try {
      setError(null);
      const options = { page, size: rowsPerPage };
      if (statusFilter) options.status = statusFilter;

      const response = await IntegrationService.listMessages(options);
      setMessages(response.content || []);
      setTotalElements(response.totalElements || 0);
    } catch (err) {
      setError(err.message);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleRetry = async (id) => {
    try {
      await IntegrationService.retryMessage(id);
      loadMessages();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMoveToDlq = async (id) => {
    try {
      await IntegrationService.moveMessageToDlq(id);
      loadMessages();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRequeue = async (id) => {
    try {
      await IntegrationService.requeueMessage(id);
      loadMessages();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
          Integration Messages
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="RECEIVED">Received</MenuItem>
              <MenuItem value="QUEUED">Queued</MenuItem>
              <MenuItem value="PROCESSING">Processing</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
              <MenuItem value="DLQ">DLQ</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={loadMessages} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, flex: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Message ID</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Type</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Channel</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Direction</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Status</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Retries</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Received At</TableCell>
              <TableCell sx={{ bgcolor: colors.surface, color: colors.textMuted }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map((msg) => (
              <TableRow key={msg.id} hover>
                <TableCell sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {msg.messageId || msg.id?.substring(0, 12)}
                </TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{msg.messageTypeCode}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{msg.channelCode}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{msg.direction}</TableCell>
                <TableCell><StatusChip status={msg.processingStatus} /></TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{msg.retryCount || 0}</TableCell>
                <TableCell sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
                  {msg.receivedAt ? new Date(msg.receivedAt).toLocaleString() : '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => setSelectedMessage(msg)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {msg.processingStatus === 'FAILED' && (
                    <>
                      <Tooltip title="Retry">
                        <IconButton size="small" onClick={() => handleRetry(msg.id)}>
                          <RetryIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move to DLQ">
                        <IconButton size="small" onClick={() => handleMoveToDlq(msg.id)}>
                          <DlqIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {msg.processingStatus === 'DLQ' && (
                    <Tooltip title="Requeue">
                      <IconButton size="small" onClick={() => handleRequeue(msg.id)}>
                        <RequeueIcon fontSize="small" color="warning" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {messages.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ color: colors.textMuted, textAlign: 'center' }}>
                  No messages found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{ color: colors.textSecondary }}
      />

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onClose={() => setSelectedMessage(null)} maxWidth="md" fullWidth>
        <DialogTitle>Message Details</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              <Typography variant="subtitle2" gutterBottom>Raw Message:</Typography>
              <Paper sx={{ p: 2, bgcolor: '#1a1a2e', maxHeight: 300, overflow: 'auto' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.rawMessage || 'No raw message available'}
                </pre>
              </Paper>
              {selectedMessage.errorMessage && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Error:</Typography>
                  <Alert severity="error">{selectedMessage.errorMessage}</Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMessage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Message Types Tab
 */
const MessageTypesTab = ({ colors }) => {
  const [messageTypes, setMessageTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null); // null = new

  const loadMessageTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const types = await IntegrationService.listMessageTypes();
      setMessageTypes(types || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessageTypes();
  }, [loadMessageTypes]);

  const handleOpenDesigner = (type) => {
    setSelectedType(type);
    setDesignerOpen(true);
  };

  const handleNewType = () => {
    setSelectedType(null);
    setDesignerOpen(true);
  };

  const handleDesignerSave = () => {
    loadMessageTypes();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
          Message Types
        </Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={loadMessageTypes} disabled={loading} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewType}>
            New Type
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted }}>Code</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Name</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Direction</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Domain</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Target Entity</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Operation</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Mappings</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messageTypes.map((type) => (
              <TableRow key={type.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpenDesigner(type)}>
                <TableCell sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                  {type.messageTypeCode}
                </TableCell>
                <TableCell sx={{ color: colors.textPrimary }}>{type.messageTypeName}</TableCell>
                <TableCell>
                  <Chip label={type.direction} size="small"
                    sx={{ bgcolor: type.direction === 'INBOUND' ? 'rgba(33,150,243,0.15)' : 'rgba(76,175,80,0.15)',
                      color: type.direction === 'INBOUND' ? '#2196f3' : '#4caf50', fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{type.domain}</TableCell>
                <TableCell sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.8rem' }}>{type.targetEntityName}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{type.targetOperation}</TableCell>
                <TableCell>
                  <Chip label={type.fieldMapping?.mappings?.length || 0} size="small"
                    sx={{ bgcolor: colors.border, color: colors.textSecondary, fontSize: '0.7rem', minWidth: 28 }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={type.isActive ? 'Active' : 'Inactive'}
                    color={type.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {messageTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ color: colors.textMuted, textAlign: 'center' }}>
                  No message types configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Message Type Designer (full-screen) */}
      {designerOpen && (
        <MessageTypeDesigner
          messageType={selectedType}
          onClose={() => setDesignerOpen(false)}
          onSave={handleDesignerSave}
          colors={colors}
        />
      )}
    </Box>
  );
};

/**
 * Flows Tab
 */
const FlowsTab = ({ colors }) => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFlows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const flowList = await IntegrationService.listFlows();
      setFlows(flowList || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
          Integration Flows
        </Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={loadFlows} disabled={loading} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} disabled>
            New Flow
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted }}>Flow Code</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Name</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Trigger</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Message Type</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Steps</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Status</TableCell>
              <TableCell sx={{ color: colors.textMuted }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flows.map((flow) => (
              <TableRow key={flow.id}>
                <TableCell sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                  {flow.flowCode}
                </TableCell>
                <TableCell sx={{ color: colors.textPrimary }}>{flow.flowName}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{flow.triggerType}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{flow.messageTypeCode || '-'}</TableCell>
                <TableCell sx={{ color: colors.textSecondary }}>{flow.steps?.length || 0}</TableCell>
                <TableCell>
                  <Chip
                    label={flow.isActive ? 'Active' : 'Inactive'}
                    color={flow.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Execute">
                    <IconButton size="small" disabled>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {flows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: colors.textMuted, textAlign: 'center' }}>
                  No flows configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Fix missing import reference
const PlayArrowIcon = RetryIcon;

/**
 * IntegrationWorkspace Component
 */
const IntegrationWorkspace = ({ colors }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [channels, setChannels] = useState([]);

  // Channel dialog state
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [channelForm, setChannelForm] = useState({
    channelCode: '',
    channelName: '',
    channelType: 'WEBHOOK',
    direction: 'INBOUND',
    protocol: 'REST',
    connectedSystemId: '',
    connectedSystemEndpointId: '',
  });
  const [channelSaving, setChannelSaving] = useState(false);
  const [connectedSystems, setConnectedSystems] = useState([]);
  const [systemEndpoints, setSystemEndpoints] = useState([]);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, channelsData, systemsData] = await Promise.all([
        IntegrationService.getStats().catch(() => ({})),
        IntegrationService.listChannels().catch(() => []),
        IntegrationService.listConnectedSystems().catch(() => []),
      ]);
      setStats(statsData);
      setChannels(channelsData);
      setConnectedSystems(systemsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load endpoints for a selected system
  const loadSystemEndpoints = useCallback(async (systemId) => {
    if (!systemId) {
      setSystemEndpoints([]);
      return;
    }
    try {
      const endpoints = await IntegrationService.listSystemEndpoints(systemId);
      setSystemEndpoints(endpoints || []);
    } catch (err) {
      console.error('Failed to load endpoints:', err);
      setSystemEndpoints([]);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Channel actions
  const handleActivateChannel = async (channelCode) => {
    try {
      await IntegrationService.activateChannel(channelCode);
      loadDashboardData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeactivateChannel = async (channelCode) => {
    try {
      await IntegrationService.deactivateChannel(channelCode);
      loadDashboardData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCheckHealth = async (channelCode) => {
    try {
      await IntegrationService.checkChannelHealth(channelCode);
      loadDashboardData();
    } catch (err) {
      setError(err.message);
    }
  };

  // Open create channel dialog
  const handleOpenChannelDialog = () => {
    setChannelForm({
      channelCode: '',
      channelName: '',
      channelType: 'WEBHOOK',
      direction: 'INBOUND',
      protocol: 'REST',
      connectedSystemId: '',
      connectedSystemEndpointId: '',
    });
    setSystemEndpoints([]);
    setChannelDialogOpen(true);
  };

  // Handle system selection in channel form
  const handleSystemChange = async (systemId) => {
    setChannelForm(prev => ({ ...prev, connectedSystemId: systemId, connectedSystemEndpointId: '' }));
    await loadSystemEndpoints(systemId);
  };

  // Create channel
  const handleCreateChannel = async () => {
    try {
      setChannelSaving(true);
      await IntegrationService.createChannel(channelForm);
      setChannelDialogOpen(false);
      loadDashboardData();
    } catch (err) {
      setError(err.message);
    } finally {
      setChannelSaving(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.background,
        overflow: 'hidden',
      }}
    >
      {/* Tab Bar */}
      <Box sx={{ borderBottom: 1, borderColor: colors.border, bgcolor: colors.surface }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { color: colors.textSecondary },
            '& .Mui-selected': { color: colors.success },
            '& .MuiTabs-indicator': { bgcolor: colors.success },
          }}
        >
          <Tab label="Dashboard" />
          <Tab label="Channels" />
          <Tab label="Message Types" />
          <Tab label="Messages" />
          <Tab label="Flows" />
          <Tab label="Inbound Simulator" />
          <Tab label="Outbound Log" />
          <Tab label="File Import" />
        </Tabs>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <DashboardTab
          colors={colors}
          stats={stats}
          channels={channels}
          loading={loading}
          onRefresh={loadDashboardData}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <ChannelsTab
          colors={colors}
          channels={channels}
          loading={loading}
          onRefresh={loadDashboardData}
          onActivate={handleActivateChannel}
          onDeactivate={handleDeactivateChannel}
          onCheckHealth={handleCheckHealth}
          onCreate={handleOpenChannelDialog}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <MessageTypesTab colors={colors} />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <MessagesTab colors={colors} loading={loading} onRefresh={loadDashboardData} />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <FlowsTab colors={colors} />
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <InboundSimulatorTab />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <OutboundLogTab />
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <FileImportTab colors={colors} />
      </TabPanel>

      {/* Create Channel Dialog */}
      <Dialog open={channelDialogOpen} onClose={() => setChannelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Channel</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Channel Code"
              value={channelForm.channelCode}
              onChange={(e) => setChannelForm({ ...channelForm, channelCode: e.target.value.toUpperCase() })}
              placeholder="e.g., ERP_SAP_INBOUND"
              fullWidth
              required
            />
            <TextField
              label="Channel Name"
              value={channelForm.channelName}
              onChange={(e) => setChannelForm({ ...channelForm, channelName: e.target.value })}
              placeholder="e.g., SAP ERP Inbound Channel"
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Channel Type</InputLabel>
              <Select
                value={channelForm.channelType}
                label="Channel Type"
                onChange={(e) => setChannelForm({ ...channelForm, channelType: e.target.value })}
              >
                <MenuItem value="WEBHOOK">Webhook</MenuItem>
                <MenuItem value="FILE">File</MenuItem>
                <MenuItem value="SCHEDULED_POLL">Scheduled Poll</MenuItem>
                <MenuItem value="MQ">Message Queue</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={channelForm.direction}
                label="Direction"
                onChange={(e) => setChannelForm({ ...channelForm, direction: e.target.value })}
              >
                <MenuItem value="INBOUND">Inbound</MenuItem>
                <MenuItem value="OUTBOUND">Outbound</MenuItem>
                <MenuItem value="BIDIRECTIONAL">Bidirectional</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={channelForm.protocol}
                label="Protocol"
                onChange={(e) => setChannelForm({ ...channelForm, protocol: e.target.value })}
              >
                <MenuItem value="REST">REST</MenuItem>
                <MenuItem value="SOAP">SOAP</MenuItem>
                <MenuItem value="EDI">EDI</MenuItem>
                <MenuItem value="FLAT_FILE">Flat File</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Connected System</InputLabel>
              <Select
                value={channelForm.connectedSystemId}
                label="Connected System"
                onChange={(e) => handleSystemChange(e.target.value)}
              >
                <MenuItem value="">-- Select System --</MenuItem>
                {connectedSystems.map((sys) => (
                  <MenuItem key={sys.id} value={sys.id}>{sys.systemCode} - {sys.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!channelForm.connectedSystemId}>
              <InputLabel>Endpoint</InputLabel>
              <Select
                value={channelForm.connectedSystemEndpointId}
                label="Endpoint"
                onChange={(e) => setChannelForm({ ...channelForm, connectedSystemEndpointId: e.target.value })}
              >
                <MenuItem value="">-- Select Endpoint --</MenuItem>
                {systemEndpoints.map((ep) => (
                  <MenuItem key={ep.id} value={ep.id}>
                    {ep.endpointType} - {ep.url} ({ep.authType || 'API_KEY'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {channelForm.connectedSystemEndpointId && systemEndpoints.find(ep => ep.id === channelForm.connectedSystemEndpointId) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Auth type will be inherited from endpoint: <strong>{systemEndpoints.find(ep => ep.id === channelForm.connectedSystemEndpointId)?.authType || 'API_KEY'}</strong>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateChannel}
            disabled={channelSaving || !channelForm.channelCode || !channelForm.channelName}
          >
            {channelSaving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationWorkspace;
